import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const fmt = (n) => Number(n || 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const firstDayMonth = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
const lastDayMonth = (d = new Date()) => {
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  return last.toISOString().slice(0, 10)
}

export default function CentrosCosto() {
  const [tab, setTab] = useState('rentabilidad')
  return (
    <AdminLayout>
      <div className="cc-wrap">
        <div className="cc-headline">
          <h1>Centros de costo</h1>
          <p>Rentabilidad por línea de negocio. Etiquetá cada asiento con el centro correspondiente.</p>
        </div>
        <div className="cc-tabs">
          <button className={tab === 'rentabilidad' ? 'active' : ''} onClick={() => setTab('rentabilidad')}>📊 Rentabilidad por línea</button>
          <button className={tab === 'centros' ? 'active' : ''} onClick={() => setTab('centros')}>⚙️ Gestión de centros</button>
          <button className={tab === 'sin_asignar' ? 'active' : ''} onClick={() => setTab('sin_asignar')}>❓ Sin asignar</button>
        </div>
        {tab === 'rentabilidad' && <Rentabilidad />}
        {tab === 'centros' && <GestionCentros />}
        {tab === 'sin_asignar' && <SinAsignar />}
      </div>
      <style>{CSS}</style>
    </AdminLayout>
  )
}

// ════════ RENTABILIDAD POR LÍNEA ════════

function Rentabilidad() {
  const [desde, setDesde] = useState(() => firstDayMonth())
  const [hasta, setHasta] = useState(() => lastDayMonth())
  const [filas, setFilas] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { cargar() }, [desde, hasta])
  async function cargar() {
    setLoading(true)
    const { data } = await supabase.rpc('get_rentabilidad_centros_costo', { p_desde: desde, p_hasta: hasta })
    setFilas(data || [])
    setLoading(false)
  }

  const totales = useMemo(() => {
    let ing = 0, egr = 0
    for (const f of filas) { ing += Number(f.ingresos); egr += Number(f.egresos) }
    return { ingresos: ing, egresos: egr, resultado: ing - egr, margen: ing > 0 ? ((ing - egr) / ing) * 100 : 0 }
  }, [filas])

  return (
    <>
      <div className="cc-toolbar">
        <label className="cc-fil"><span>Desde</span><input type="date" value={desde} onChange={e => setDesde(e.target.value)} /></label>
        <label className="cc-fil"><span>Hasta</span><input type="date" value={hasta} onChange={e => setHasta(e.target.value)} /></label>
        <button className="cc-btn-ghost" onClick={() => { setDesde(firstDayMonth()); setHasta(lastDayMonth()) }}>Este mes</button>
        <button className="cc-btn-ghost" onClick={() => {
          const d = new Date(); d.setMonth(d.getMonth() - 1)
          setDesde(firstDayMonth(d)); setHasta(lastDayMonth(d))
        }}>Mes anterior</button>
        <button className="cc-btn-ghost" onClick={() => {
          const d = new Date()
          setDesde(`${d.getFullYear()}-01-01`); setHasta(new Date().toISOString().slice(0, 10))
        }}>Año actual</button>
      </div>

      <div className="cc-kpis">
        <KpiCard label="Ingresos totales" value={'$' + fmt(totales.ingresos)} color="#16a34a" />
        <KpiCard label="Egresos totales" value={'$' + fmt(totales.egresos)} color="#dc2626" />
        <KpiCard label="Resultado" value={'$' + fmt(totales.resultado)} color={totales.resultado >= 0 ? '#16a34a' : '#dc2626'} sub={totales.resultado >= 0 ? '✓ Utilidad' : '⚠ Pérdida'} />
        <KpiCard label="Margen consolidado" value={totales.margen.toFixed(1) + '%'} color="#7c3aed" />
      </div>

      {loading ? <div className="cc-loading">Cargando…</div> : (
        <div className="cc-grid">
          {filas.map(f => (
            <div className="cc-card" key={f.codigo} style={{ borderColor: f.color + '40' }}>
              <div className="cc-card-head">
                <div className="cc-card-emoji">{f.emoji}</div>
                <div className="cc-card-title">{f.nombre}</div>
                <div className="cc-card-asi">{f.cant_asientos} asientos</div>
              </div>
              <div className="cc-card-monto">
                <div className="cc-card-monto-label">Resultado</div>
                <div className="cc-card-monto-val" style={{ color: f.resultado >= 0 ? '#16a34a' : '#dc2626' }}>${fmt(f.resultado)}</div>
                <div className="cc-card-margen" style={{ color: f.margen_pct >= 0 ? '#16a34a' : '#dc2626' }}>
                  {Number(f.margen_pct).toFixed(1)}% margen
                </div>
              </div>
              <div className="cc-card-detalles">
                <div><span>Ingresos</span><b style={{ color: '#16a34a' }}>${fmt(f.ingresos)}</b></div>
                <div><span>Egresos</span><b style={{ color: '#dc2626' }}>${fmt(f.egresos)}</b></div>
                {totales.ingresos > 0 && (
                  <div className="cc-card-bar">
                    <div className="cc-card-bar-label">{((Number(f.ingresos) / totales.ingresos) * 100).toFixed(1)}% del total ingresos</div>
                    <div className="cc-card-bar-bg">
                      <div className="cc-card-bar-fill" style={{ width: `${(Number(f.ingresos) / totales.ingresos) * 100}%`, background: f.color }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="cc-info">
        <strong>📚 Cómo se asignan los centros de costo</strong>
        <ul>
          <li><b>Cobros B2C suscripción</b> (pagos vinculados a entrega) → automático <b>B2C Suscripción</b></li>
          <li><b>Cobros B2C tienda one-shot</b> (sin entrega) → automático <b>B2C Tienda</b></li>
          <li><b>Gastos fijos</b> (BPS, IRAE, Biller, sueldos): asignar manualmente según corresponda</li>
          <li>Asientos sin etiqueta → pestaña "Sin asignar" para clasificarlos manualmente</li>
        </ul>
      </div>
    </>
  )
}

// ════════ GESTIÓN DE CENTROS ════════

function GestionCentros() {
  const [centros, setCentros] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)

  useEffect(() => { cargar() }, [])
  async function cargar() {
    setLoading(true)
    const { data } = await supabase.from('centros_costo').select('*').order('orden')
    setCentros(data || [])
    setLoading(false)
  }

  async function guardar(form) {
    try {
      const payload = { codigo: form.codigo, nombre: form.nombre, descripcion: form.descripcion, color: form.color, emoji: form.emoji, tipo: form.tipo, activo: !!form.activo, orden: Number(form.orden) }
      if (form.isEdit) await supabase.from('centros_costo').update(payload).eq('codigo', form.codigo_original)
      else await supabase.from('centros_costo').insert(payload)
      setModal(null)
      cargar()
    } catch (e) { alert('Error: ' + e.message) }
  }

  if (loading) return <div className="cc-loading">Cargando…</div>

  return (
    <>
      <div className="cc-toolbar">
        <button className="cc-btn-primary" onClick={() => setModal({ codigo: '', nombre: '', descripcion: '', color: '#1A3461', emoji: '', tipo: 'mixto', activo: true, orden: centros.length })}>+ Nuevo centro</button>
      </div>

      <div className="cc-tabla-wrap">
        <table className="cc-tabla">
          <thead>
            <tr>
              <th></th>
              <th>Código</th>
              <th>Nombre</th>
              <th>Tipo</th>
              <th>Descripción</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {centros.map(c => (
              <tr key={c.codigo} className={c.activo ? '' : 'cc-inactivo'}>
                <td style={{ width: 50 }}><span style={{ fontSize: 22 }}>{c.emoji}</span></td>
                <td><code>{c.codigo}</code></td>
                <td><b style={{ color: c.color }}>{c.nombre}</b></td>
                <td><span className="cc-pill" style={{ background: c.color + '20', color: c.color }}>{c.tipo}</span></td>
                <td style={{ fontSize: 12, color: '#64748b' }}>{c.descripcion}</td>
                <td>{c.activo ? <span className="cc-pill-ok">Activo</span> : <span className="cc-pill-inac">Inactivo</span>}</td>
                <td><button className="cc-btn-edit" onClick={() => setModal({ ...c, isEdit: true, codigo_original: c.codigo })}>Editar</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && <ModalCentro initial={modal} onClose={() => setModal(null)} onSave={guardar} />}
    </>
  )
}

function ModalCentro({ initial, onClose, onSave }) {
  const [f, setF] = useState(initial)
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value })

  return (
    <div className="cc-modal-bg" onClick={onClose}>
      <div className="cc-modal" onClick={e => e.stopPropagation()}>
        <button className="cc-modal-close" onClick={onClose}>×</button>
        <h2>{f.isEdit ? 'Editar centro' : 'Nuevo centro de costo'}</h2>
        <form onSubmit={e => { e.preventDefault(); onSave(f) }}>
          <div className="cc-form-row">
            <Field label="Código *"><input type="text" value={f.codigo} onChange={upd('codigo')} required disabled={f.isEdit} placeholder="ej: b2c_susc" /></Field>
            <Field label="Emoji"><input type="text" value={f.emoji} onChange={upd('emoji')} placeholder="🔁" /></Field>
          </div>
          <Field label="Nombre *"><input type="text" value={f.nombre} onChange={upd('nombre')} required /></Field>
          <Field label="Descripción"><textarea rows={2} value={f.descripcion || ''} onChange={upd('descripcion')} /></Field>
          <div className="cc-form-row">
            <Field label="Tipo">
              <select value={f.tipo} onChange={upd('tipo')}>
                <option value="ingreso">Ingreso</option>
                <option value="egreso">Egreso</option>
                <option value="mixto">Mixto</option>
              </select>
            </Field>
            <Field label="Color"><input type="color" value={f.color} onChange={upd('color')} /></Field>
            <Field label="Orden"><input type="number" value={f.orden} onChange={upd('orden')} /></Field>
          </div>
          <Field label="">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!f.activo} onChange={e => setF({ ...f, activo: e.target.checked })} /> Activo
            </label>
          </Field>
          <div className="cc-modal-actions">
            <button type="button" className="cc-btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="cc-btn-primary">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ════════ ASIENTOS SIN ASIGNAR ════════

function SinAsignar() {
  const [asientos, setAsientos] = useState([])
  const [centros, setCentros] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { cargar() }, [])
  async function cargar() {
    setLoading(true)
    const [{ data: a }, { data: c }] = await Promise.all([
      supabase.from('asientos_contables').select('id, numero, fecha, concepto, monto_total, origen_tabla').is('centro_costo_codigo', null).eq('estado', 'confirmado').order('fecha', { ascending: false }).limit(100),
      supabase.from('centros_costo').select('codigo, nombre, emoji, color').eq('activo', true).order('orden'),
    ])
    setAsientos(a || [])
    setCentros(c || [])
    setLoading(false)
  }

  async function asignar(asientoId, codigo) {
    try {
      await supabase.from('asientos_contables').update({ centro_costo_codigo: codigo }).eq('id', asientoId)
      cargar()
    } catch (e) { alert('Error: ' + e.message) }
  }

  if (loading) return <div className="cc-loading">Cargando…</div>
  if (asientos.length === 0) return <div className="cc-empty"><strong>🎉 Todos los asientos están asignados</strong><p>No hay asientos pendientes de clasificar.</p></div>

  return (
    <>
      <p className="cc-h2-sub">Asignále un centro de costo a cada asiento. Filtramos los 100 más recientes sin etiquetar.</p>
      <div className="cc-tabla-wrap">
        <table className="cc-tabla">
          <thead>
            <tr>
              <th>#</th>
              <th>Fecha</th>
              <th>Concepto</th>
              <th>Origen</th>
              <th style={{ textAlign: 'right' }}>Monto</th>
              <th>Asignar centro</th>
            </tr>
          </thead>
          <tbody>
            {asientos.map(a => (
              <tr key={a.id}>
                <td style={{ fontSize: 11, color: '#64748b' }}>#{a.numero}</td>
                <td>{a.fecha}</td>
                <td>{a.concepto}</td>
                <td style={{ fontSize: 11, color: '#64748b' }}>{a.origen_tabla}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>${fmt(a.monto_total)}</td>
                <td>
                  <select onChange={e => e.target.value && asignar(a.id, e.target.value)} defaultValue="">
                    <option value="">— Elegir —</option>
                    {centros.map(c => <option key={c.codigo} value={c.codigo}>{c.emoji} {c.nombre}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

function Field({ label, children }) { return <label className="cc-field"><span>{label}</span>{children}</label> }
function KpiCard({ label, value, sub, color }) {
  return <div className="cc-kpi"><div className="cc-kpi-l">{label}</div><div className="cc-kpi-v" style={{ color }}>{value}</div>{sub && <div className="cc-kpi-s">{sub}</div>}</div>
}

const CSS = `
.cc-wrap { padding: 16px 20px 32px; max-width: 1400px; margin: 0 auto }
.cc-headline h1 { font-size: 26px; font-weight: 900; color: #1A3461; margin: 0 0 4px; letter-spacing: -.02em }
.cc-headline p { font-size: 13px; color: #64748b; margin: 0 0 16px }
.cc-tabs { display: flex; gap: 4px; margin-bottom: 16px; border-bottom: 2px solid #e2e8f0; flex-wrap: wrap }
.cc-tabs button { background: transparent; border: 0; padding: 10px 16px; font-size: 13px; font-weight: 700; color: #64748b; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -2px; font-family: inherit }
.cc-tabs button.active { color: #1A3461; border-bottom-color: #1A3461 }
.cc-toolbar { display: flex; gap: 10px; align-items: flex-end; margin-bottom: 14px; flex-wrap: wrap }
.cc-fil { display: flex; flex-direction: column; gap: 3px }
.cc-fil span { font-size: 11px; color: #475569; font-weight: 700; text-transform: uppercase; letter-spacing: .04em }
.cc-fil input { padding: 7px 10px; border: 1px solid #cbd5e1; border-radius: 7px; font-size: 13px; font-family: inherit }
.cc-btn-primary { background: #1A3461; color: #fff; border: 0; padding: 9px 16px; border-radius: 8px; font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit }
.cc-btn-secondary { background: #fff; border: 1px solid #cbd5e1; color: #475569; padding: 9px 16px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit }
.cc-btn-ghost { background: transparent; border: 1px solid #cbd5e1; color: #475569; padding: 7px 12px; border-radius: 7px; font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit }
.cc-btn-edit { background: transparent; border: 1px solid #cbd5e1; color: #475569; padding: 4px 10px; border-radius: 5px; font-size: 11px; font-weight: 700; cursor: pointer; font-family: inherit }
.cc-loading, .cc-empty { padding: 40px; text-align: center; color: #94a3b8; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px }
.cc-empty strong { display: block; color: #1A3461; font-size: 15px; margin-bottom: 6px }
.cc-h2-sub { font-size: 13px; color: #64748b; margin: 0 0 14px }

.cc-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; margin-bottom: 16px }
.cc-kpi { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px }
.cc-kpi-l { font-size: 11px; color: #94a3b8; text-transform: uppercase; font-weight: 800; letter-spacing: .04em }
.cc-kpi-v { font-size: 22px; font-weight: 900 }
.cc-kpi-s { font-size: 11px; color: #64748b }

.cc-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 12px }
.cc-card { background: #fff; border: 2px solid; border-radius: 12px; padding: 16px; display: flex; flex-direction: column; gap: 12px }
.cc-card-head { display: flex; align-items: center; gap: 10px }
.cc-card-emoji { font-size: 28px }
.cc-card-title { flex: 1; font-weight: 900; color: #1A3461; font-size: 14px }
.cc-card-asi { font-size: 11px; color: #94a3b8; font-weight: 700 }
.cc-card-monto { text-align: center; padding: 14px 0; border-top: 1px solid #f1f5f9; border-bottom: 1px solid #f1f5f9 }
.cc-card-monto-label { font-size: 11px; color: #94a3b8; text-transform: uppercase; font-weight: 800 }
.cc-card-monto-val { font-size: 26px; font-weight: 900; letter-spacing: -.02em; margin: 2px 0 }
.cc-card-margen { font-size: 12px; font-weight: 700 }
.cc-card-detalles { font-size: 13px }
.cc-card-detalles > div { display: flex; justify-content: space-between; padding: 3px 0 }
.cc-card-bar { margin-top: 10px }
.cc-card-bar-label { font-size: 11px; color: #64748b; margin-bottom: 4px }
.cc-card-bar-bg { height: 6px; background: #f1f5f9; border-radius: 999px; overflow: hidden }
.cc-card-bar-fill { height: 100%; border-radius: 999px; transition: width .3s }

.cc-tabla-wrap { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; overflow: auto }
.cc-tabla { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 700px }
.cc-tabla th { text-align: left; padding: 10px 12px; background: #f8fafc; font-weight: 700; color: #475569; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e2e8f0 }
.cc-tabla td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; color: #1A3461 }
.cc-tabla code { background: #e0e7ff; color: #3730a3; padding: 1px 6px; border-radius: 3px; font-size: 11.5px }
.cc-tabla select { padding: 6px 10px; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 12px; font-family: inherit }
.cc-inactivo { opacity: .5 }
.cc-pill { padding: 3px 8px; border-radius: 5px; font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em }
.cc-pill-ok { background: #d1fae5; color: #065f46; padding: 3px 8px; border-radius: 5px; font-size: 10.5px; font-weight: 800 }
.cc-pill-inac { background: #f1f5f9; color: #64748b; padding: 3px 8px; border-radius: 5px; font-size: 10.5px; font-weight: 800 }

.cc-info { margin-top: 18px; padding: 14px 18px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; font-size: 12.5px; color: #1e3a8a; line-height: 1.6 }
.cc-info strong { font-size: 13px }
.cc-info ul { margin: 8px 0; padding-left: 22px }
.cc-info b { color: #1e40af }

.cc-modal-bg { position: fixed; inset: 0; background: rgba(15,31,61,.55); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 16px }
.cc-modal { background: #fff; border-radius: 14px; max-width: 580px; width: 100%; max-height: 92vh; overflow-y: auto; padding: 26px 28px; position: relative }
.cc-modal h2 { margin: 0 0 14px; font-size: 19px; color: #1A3461; font-weight: 900 }
.cc-modal-close { position: absolute; top: 12px; right: 12px; background: none; border: 0; font-size: 22px; cursor: pointer; color: #64748b }
.cc-modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 16px; padding-top: 14px; border-top: 1px solid #e2e8f0 }
.cc-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px }
.cc-form-row:has(:nth-child(3)) { grid-template-columns: 1fr 100px 100px }
.cc-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px }
.cc-field span { font-size: 12px; font-weight: 700; color: #475569 }
.cc-field input, .cc-field select, .cc-field textarea { padding: 9px 11px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 14px; font-family: inherit; color: #1A3461 }
`
